import { createStore, useStore } from "zustand";
import type { GridState } from "./grid-utils";

interface GridStoreState {
  cells: GridState;
  initialCells: GridState;
  slotsPerDay: number;
  granularity: number;
  numDays: number;
  startDate: string;
  // Drag state — lives in the store so individual cells can subscribe
  // to their own drag status without re-rendering the entire grid.
  pageOffset: number;
  isDragging: boolean;
  draggedCells: Record<string, boolean>;
  dragEndedAt: number;
  // Cell actions
  setCells: (cells: GridState) => void;
  toggleCell: (key: string) => void;
  batchUpdate: (updates: GridState) => void;
  resetCells: () => void;
  setSlotsPerDay: (n: number) => void;
  setNumDays: (n: number) => void;
  setStartDate: (iso: string) => void;
  setPageOffset: (offset: number) => void;
  // Drag actions
  addDraggedCell: (key: string) => void;
  startDrag: () => void;
  endDrag: () => void;
}

export type GridStore = ReturnType<typeof createGridStore>;

export function createGridStore(
  initialCells: GridState = {},
  initialSlotsPerDay = 24,
  initialGranularity = 60,
  initialNumDays = 7,
  initialStartDate = "",
) {
  return createStore<GridStoreState>()((set, get) => ({
    cells: initialCells,
    initialCells,
    slotsPerDay: initialSlotsPerDay,
    granularity: initialGranularity,
    numDays: initialNumDays,
    startDate: initialStartDate,
    pageOffset: 0,
    isDragging: false,
    draggedCells: {},
    dragEndedAt: 0,
    setCells: (cells) => set({ cells }),
    toggleCell: (key) => set((state) => ({ cells: { ...state.cells, [key]: !state.cells[key] } })),
    batchUpdate: (updates) => set((state) => ({ cells: { ...state.cells, ...updates } })),
    resetCells: () => set({ cells: { ...get().initialCells } }),
    setSlotsPerDay: (n) => set({ slotsPerDay: n }),
    setNumDays: (n) => set({ numDays: n }),
    setStartDate: (iso) => set({ startDate: iso }),
    setPageOffset: (offset) => set({ pageOffset: offset }),
    addDraggedCell: (key) =>
      set((state) => (state.draggedCells[key] ? state : { draggedCells: { ...state.draggedCells, [key]: true } })),
    startDrag: () => set({ isDragging: true, draggedCells: {} }),
    endDrag: () => set({ isDragging: false, draggedCells: {}, dragEndedAt: Date.now() }),
  }));
}

/**
 * Hook for a cell to subscribe to its own value in the grid store.
 * Zustand's selector equality check (Object.is on booleans) ensures only
 * the cell whose value changed will re-render.
 */
export function useCellValue(store: GridStore, day: number | string, hour: number): boolean {
  return useStore(store, (state) => !!state.cells[`${day}-${hour}`]);
}

/**
 * Hook for a cell to know whether it should be visible at the current
 * granularity.  The selector returns a boolean, so Zustand's Object.is
 * check means only cells at the visibility boundary re-render when
 * slotsPerDay changes — cells that were already visible (or invisible)
 * stay put.
 */
export function useCellVisible(store: GridStore, hour: number): boolean {
  return useStore(store, (state) => hour < state.slotsPerDay);
}

/**
 * Hook for a cell to subscribe to its own drag status.
 * Only the cell being dragged over will re-render — other cells stay put.
 */
export function useCellDragged(store: GridStore, day: number | string, hour: number): boolean {
  return useStore(store, (state) => !!state.draggedCells[`${day}-${hour}`]);
}

/**
 * Hook to subscribe to slotsPerDay changes.
 * Used by DayColumn to clip its height — only 7 components re-render
 * on granularity switch instead of hundreds of individual cells.
 */
export function useSlotsPerDay(store: GridStore): number {
  return useStore(store, (state) => state.slotsPerDay);
}

/**
 * Hook to subscribe to granularity changes.
 * Returns a number — cast to Granularity at call site if needed.
 */
export function useGranularity(store: GridStore): number {
  return useStore(store, (state) => state.granularity);
}

/**
 * Hook to subscribe to numDays changes.
 * Only components using this hook re-render when the cycle length changes.
 */
export function useNumDays(store: GridStore): number {
  return useStore(store, (state) => state.numDays);
}

/**
 * Hook to subscribe to startDate changes (ISO string).
 * Only the date picker component re-renders when the date changes.
 */
export function useStartDate(store: GridStore): string {
  return useStore(store, (state) => state.startDate);
}

export function usePageOffset(store: GridStore): number {
  return useStore(store, (state) => state.pageOffset);
}
